import React, { useEffect, useMemo, useState } from 'react';
import { TaskHoliday } from '../../types/tasks';
import { taskApi, taskErrorMessage } from '../../utils/taskApi';
import { businessToday, weekdayDate } from '../../utils/taskDates';
import TaskIcon from './TaskIcon';
import '../../tasks.css';

/**
 * Admin › Holidays: days the business is closed. No morning digest goes out
 * on them, and the "Holiday reminders" rule puts "Set holiday notification on
 * website / voicemail" on the week list ahead of each one. Dec 24 – Jan 2 is
 * always closed and isn't listed here.
 */
const HolidaysSettings: React.FC = () => {
  const today = businessToday();
  const thisYear = Number(today.slice(0, 4));
  const [holidays, setHolidays] = useState<TaskHoliday[] | null>(null);
  const [day, setDay] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    taskApi
      .holidays()
      .then((r) => setHolidays(r.holidays))
      .catch((e) => setError(taskErrorMessage(e, 'Could not load holidays')));
  }, []);

  const run = async (fn: () => Promise<{ holidays: TaskHoliday[] }>) => {
    setBusy(true);
    setError(null);
    try {
      setHolidays((await fn()).holidays);
      return true;
    } catch (e) {
      setError(taskErrorMessage(e, 'Could not save'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    if (await run(() => taskApi.createHoliday(day, name.trim()))) {
      setDay('');
      setName('');
    }
  };

  const byYear = useMemo(() => {
    const groups = new Map<string, TaskHoliday[]>();
    (holidays || []).forEach((h) => groups.set(h.day.slice(0, 4), [...(groups.get(h.day.slice(0, 4)) || []), h]));
    return Array.from(groups.entries());
  }, [holidays]);
  const hasYear = (year: number) => (holidays || []).some((h) => h.day.startsWith(String(year)));

  return (
    <div className="th-settings">
      <p className="th-break">
        <TaskIcon name="calendar" size={14} /> <strong>Dec 24 – Jan 2</strong> is always treated as closed — no need to add it.
      </p>

      <form className="th-add" onSubmit={add}>
        <input type="date" className="tk-input th-date" value={day} onChange={(e) => setDay(e.target.value)} aria-label="Holiday date" />
        <input className="tk-input th-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Name, e.g. Family Day" aria-label="Holiday name" />
        <button type="submit" className="action-button" disabled={busy || !day || !name.trim()}>Add</button>
      </form>

      <div className="th-defaults">
        {[thisYear, thisYear + 1].map((year) => (
          <button
            key={year}
            type="button"
            className="tk-btn tk-btn-ghost tk-btn-sm"
            disabled={busy}
            onClick={() => run(() => taskApi.addDefaultHolidays(year))}
            title="Family Day, Good Friday, Victoria Day, Canada Day, Civic Holiday, Labour Day, Thanksgiving — remove any you don't close for"
          >
            <TaskIcon name="plus" size={13} /> {hasYear(year) ? `Fill in missing ${year} Ontario holidays` : `Add Ontario holidays for ${year}`}
          </button>
        ))}
      </div>

      {error && <p className="error-text">{error}</p>}
      {!holidays && !error && <p className="section-description">Loading…</p>}
      {holidays && !holidays.length && <p className="section-description">No holidays added yet.</p>}

      {byYear.map(([year, rows]) => (
        <div key={year} className="th-year">
          <h4 className="tk-section-label">{year}</h4>
          <ul className="th-list">
            {rows.map((h) => (
              <li key={h.id} className={`th-row${h.day < today ? ' is-past' : ''}`}>
                <span className="th-row-date">{weekdayDate(h.day)}</span>
                <span className="th-row-name">{h.name}</span>
                <button
                  type="button"
                  className="tk-icon-btn tk-icon-btn-sm"
                  onClick={() => run(() => taskApi.deleteHoliday(h.id))}
                  disabled={busy}
                  aria-label={`Remove ${h.name}`}
                  title="Remove"
                >
                  <TaskIcon name="close" size={13} />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default HolidaysSettings;
